import _ from "lodash";

export const doesChildElementFitInsideParentWidth = (
  parentElement: HTMLElement, 
  childElement: HTMLElement,
  buffer: number = 4  // to avoid issues with rounding and one pixel during zooming
) : boolean => {
  if (!parentElement || !childElement) return false;

  var parentElementRect = parentElement.getBoundingClientRect();
  var childElementRect = childElement.getBoundingClientRect();

  let result = parentElementRect.left <= childElementRect.left && parentElementRect.right + buffer >= childElementRect.right;
  
  return result;
};

export const doesChildElementFitInsideParentHeight = (
  parentElement: HTMLElement, 
  childElement: HTMLElement,
  buffer: number = 4  // to avoid issues with rounding and one pixel during zooming
) : boolean => {
  if (!parentElement || !childElement) return false;

  var parentElementRect = parentElement.getBoundingClientRect();
  var childElementRect = childElement.getBoundingClientRect();

  let result = parentElementRect.top <= childElementRect.top && parentElementRect.bottom + buffer >= childElementRect.bottom;
  
  return result;
};

export const doChildElementsFitInsideParentWidth = (
  parentElement: HTMLElement | null,
  childElements: (HTMLElement | null)[],
  buffer: number = 0
) : boolean => {

  if (!parentElement || !childElements || childElements.length === 0) return false;

  const parentElementRect = parentElement.getBoundingClientRect();
  const parentWidth = parentElementRect.width;

  // Calculate the total width of all child elements
  let totalChildWidth = 0;

  for (const child of childElements) {
    if (child) { 
      const childRect = child.getBoundingClientRect();
      totalChildWidth += childRect.width;
    }
  }

  // Check if the combined width of children plus the buffer fits within the parent
  const result = totalChildWidth + buffer <= parentWidth;
  
  return result;
};

export const getHtmlElementWidthWithMargin = (element: HTMLElement) : number => {
  const elementStyle = getComputedStyle(element);
  if (!elementStyle) return 0;
  return parseFloat(elementStyle.width) + parseFloat(elementStyle.marginLeft) + parseFloat(elementStyle.marginRight);
};

export const getParentHtmlElements = (element: HTMLElement, selector: string | undefined) : HTMLElement[] | undefined => {
	let parentsArray = [];
  let parent = element.parentElement;

  if (selector) {
    while (parent) {
      if (parent.matches(selector)) {
        parentsArray.push(parent);
      }
      parent = parent.parentElement;
    }
  }
  else {
    while (parent) {
      parentsArray.push(parent);
      parent = parent.parentElement;
    }
  }

	return !parentsArray.length ? undefined : parentsArray;
};

export const getActiveChildElementOnScroll = (scrollableElement: HTMLElement) : HTMLElement | undefined => {
  if (!scrollableElement || !scrollableElement.children || scrollableElement.children.length === 0) return undefined;

  const scrollableElementRect = scrollableElement.getBoundingClientRect();
  const scrollableElementTop = scrollableElementRect.top;
  const scrollableElementBottom = scrollableElementRect.bottom;

  const findFirstVisibleElement = (element: HTMLElement): HTMLElement | undefined => {
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top;

    if (elementTop >= scrollableElementTop && elementTop <= scrollableElementBottom) {
      return element;
    }

    const nestedChildren = Array.from(element.children) as HTMLElement[];
    for (const nestedChild of nestedChildren) {
      const visibleElement = findFirstVisibleElement(nestedChild);
      if (visibleElement) {
        return visibleElement;
      }
    }

    return undefined;
  };

  const childElements = Array.from(scrollableElement.children) as HTMLElement[];
  for (const child of childElements) {
    const visibleElement = findFirstVisibleElement(child);
    if (visibleElement) {
      return visibleElement; // Return the first visible child or nested child element
    }
  }

  return undefined; 
};

// Check if the element's width is less than its scrollWidth
export const hasHtmlElementOverflow = (elementId: string) => {
  if (!elementId) {
    return false;
  }

  const titleElement = document.getElementById(elementId);
  if (!titleElement) {
    return false;
  }

  return titleElement.offsetWidth < titleElement.scrollWidth;
};

// Checks if the text fits within the specified maxWidth
export const doesTextFitWithinMaxWidth = (text: string, maxWidth: number = 136, fontSize: number = 14): boolean => {
  if (!text) return true;

  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.style.position = 'absolute';
  span.style.whiteSpace = 'nowrap';
  span.style.fontSize = fontSize.toString() + 'px';
  span.textContent = text;
  document.body.appendChild(span);
  const fits = span.offsetWidth <= maxWidth;
  document.body.removeChild(span);

  return fits;
};

export const getRandomHtmlElementId = (prefix: string = '') : string => {
  return prefix
    ? `${prefix}-${crypto.randomUUID()}`
    : crypto.randomUUID();
};